"use client";

import { useAuth } from "@/contexts/AuthContext";
import { useFrontendPermissions } from "@/hooks/useFrontendPermissions";
import { useColorMode } from "@/contexts/ColorModeContext";
import {
  Navigation,
  NavigationItem,
  NavigationItemDropdown,
  DropdownItem,
  Avatar,
  Drawer,
  Toggle,
  Button,
  IconButton,
  InputText,
} from "@/design-system";
import { Icon } from "@/design-system/icons";
import type { IconName } from "@/design-system/icons";
import Link from "next/link";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { SurveyLoopLogo } from "@/components/SurveyLoopLogo";
import { SupportChatWidget } from "@/components/SupportChatWidget";
import { AuthenticatedActivityTracker } from "@/components/AuthenticatedActivityTracker";
import { GuidedOnboardingPanel } from "@/components/GuidedOnboardingPanel";
import { NotificationCenter } from "@/components/NotificationCenter";
import { useOrganization } from "@/contexts/OrganizationContext";
import { LoopyPageContextProvider } from "@/contexts/LoopyPageContext";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import {
  beginAuthSessionTransition,
  setAccessToken,
  switchActiveOrganization,
} from "@/utils/api";
import { useQueryClient } from "@tanstack/react-query";
import {
  GOOGLE_REVIEWS_LISTINGS_FEATURE_FLAG,
  IN_APP_NOTIFICATIONS_FEATURE_FLAG,
  isFeatureEnabledForOrganizationId,
} from "@/lib/featureFlags";
import { trackActivity } from "@/lib/activityTracking";
import {
  POST_SWITCH_FALLBACK_PATH,
  isNotFoundError,
  walkBackOrFallback,
} from "@/utils/orgSwitch";

interface AuthenticatedLayoutProps {
  children: React.ReactNode;
}

export const AuthenticatedLayout: React.FC<AuthenticatedLayoutProps> = ({
  children,
}) => {
  const { user, logout, isImpersonating, stopImpersonating, checkAuth } = useAuth();
  const { canManageOrgResources } = useFrontendPermissions();
  const { isDarkMode, toggleColorMode } = useColorMode();
  const pathname = usePathname();
  const router = useRouter();
  const { currentOrganization, clearSelectedOrganization } = useOrganization();
  const queryClient = useQueryClient();
  // Latest pathname is read inside the post-switch 404 listener; storing in a
  // ref avoids re-creating the subscription whenever the route changes.
  const pathnameRef = useRef(pathname);
  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  const isWhiteLabelEnabled = useMemo(() => {
    const wl = (
      currentOrganization as unknown as {
        theme?: { whitelabel?: boolean | string };
      }
    )?.theme?.whitelabel;
    return wl === true || wl === "true";
  }, [currentOrganization]);

  useEffect(() => {
    if (!isWhiteLabelEnabled) return;
    const theme = (
      currentOrganization as unknown as { theme?: Record<string, unknown> }
    )?.theme;
    if (!theme) return;

    const root = document.documentElement;
    const applied: Array<{ name: string; prev: string | null }> = [];

    Object.entries(theme).forEach(([key, value]) => {
      if (!key.startsWith("--")) return;
      const stringValue = typeof value === "string" ? value : String(value);
      const previous = root.style.getPropertyValue(key);
      applied.push({ name: key, prev: previous });
      root.style.setProperty(key, stringValue);
    });

    return () => {
      applied.forEach(({ name, prev }) => {
        if (prev && prev.length > 0) {
          root.style.setProperty(name, prev);
        } else {
          root.style.removeProperty(name);
        }
      });
    };
  }, [currentOrganization, isWhiteLabelEnabled]);

  const handleLogout = async () => {
    await logout();
  };

  const isMobile = useMediaQuery("(max-width: 880px)");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [isSwitchingOrganization, setIsSwitchingOrganization] = useState(false);
  const [orgSearchQuery, setOrgSearchQuery] = useState("");

  const navigateAndClose = useCallback(
    (href: string) => {
      setMobileNavOpen(false);
      router.push(href);
    },
    [router],
  );

  const currentOrganizationId = currentOrganization?._id || currentOrganization?.id || user?.activeOrganization || user?.organization;
  const googleReviewsListingsEnabled = isFeatureEnabledForOrganizationId(
    user,
    currentOrganizationId,
    currentOrganization,
    GOOGLE_REVIEWS_LISTINGS_FEATURE_FLAG,
  );
  const inAppNotificationsEnabled = isFeatureEnabledForOrganizationId(
    user,
    currentOrganizationId,
    currentOrganization,
    IN_APP_NOTIFICATIONS_FEATURE_FLAG,
  );

  const navLinks: { href: string; label: string; icon: IconName; isActive: boolean }[] = [
    { href: "/dashboard", label: "Dashboard", icon: "Home", isActive: pathname === "/dashboard" },
    { href: "/surveys", label: "Surveys", icon: "FileText", isActive: pathname === "/surveys" || pathname.startsWith("/surveys/") },
    { href: "/responses", label: "Responses", icon: "BarChart", isActive: pathname.startsWith("/responses") },
    ...(canManageOrgResources && googleReviewsListingsEnabled
      ? [
          { href: "/reviews", label: "Reviews", icon: "Star" as IconName, isActive: pathname.startsWith("/reviews") },
          { href: "/listings", label: "Listings", icon: "MapPinned" as IconName, isActive: pathname.startsWith("/listings") },
        ]
      : []),
    { href: "/analytics", label: "Analytics", icon: "BarChart3", isActive: pathname.startsWith("/analytics") },
  ];
  const supportChatEnabled = true;
  const surveyLoopAgentEnabled = true;

  const getOrganizationLabel = useCallback(
    (organization: { name?: string | null }) => {
      const name = organization.name?.trim();
      return name && name.length > 0 ? name : "Unnamed organization";
    },
    [],
  );

  const userOrganizations = useMemo(
    () =>
      [...(user?.organizations || [])].sort((a, b) => {
        if (a.isHomeOrganization && !b.isHomeOrganization) return -1;
        if (!a.isHomeOrganization && b.isHomeOrganization) return 1;
        return getOrganizationLabel(a).localeCompare(getOrganizationLabel(b));
      }),
    [getOrganizationLabel, user?.organizations],
  );

  // Match InputSelect convention: only show search when there are enough items.
  const isOrgSearchEnabled = userOrganizations.length > 6;

  const filteredUserOrganizations = useMemo(() => {
    const query = orgSearchQuery.trim().toLowerCase();
    if (!isOrgSearchEnabled || query.length === 0) return userOrganizations;
    return userOrganizations.filter((organization) =>
      getOrganizationLabel(organization).toLowerCase().includes(query),
    );
  }, [getOrganizationLabel, isOrgSearchEnabled, orgSearchQuery, userOrganizations]);

  // Focus the search input when the dropdown menu mounts (and reset the
  // query so a fresh search starts each time the switcher is reopened).
  const orgSearchRef = useCallback((el: HTMLInputElement | null) => {
    if (!el) return;
    setOrgSearchQuery("");
    requestAnimationFrame(() => el.focus());
  }, []);

  const handleSwitchOrganization = useCallback(
    async (organizationId: string) => {
      if (!organizationId || isSwitchingOrganization) return;
      setIsSwitchingOrganization(true);

      // Listener that reacts to 404s coming back from queries that refetch
      // immediately after the org switch. We allow at most two hops (e.g.
      // /surveys/abc/edit -> /surveys/abc -> /surveys) before giving up
      // and routing to the dashboard. The window is also bounded in time
      // so we never leak the subscription if no queries fire.
      let hopsRemaining = 2;
      let unsubscribe: (() => void) | null = null;
      let teardownTimer: ReturnType<typeof setTimeout> | null = null;

      const teardownListener = () => {
        if (unsubscribe) {
          unsubscribe();
          unsubscribe = null;
        }
        if (teardownTimer) {
          clearTimeout(teardownTimer);
          teardownTimer = null;
        }
      };

      const handle404 = () => {
        const currentPath = pathnameRef.current || "/";
        const nextPath = walkBackOrFallback(currentPath);

        if (hopsRemaining <= 0 || nextPath === currentPath) {
          teardownListener();
          if (currentPath !== POST_SWITCH_FALLBACK_PATH) {
            router.replace(POST_SWITCH_FALLBACK_PATH);
          }
          return;
        }

        hopsRemaining -= 1;
        // If we just walked into the dashboard (or any terminal fallback),
        // there is nowhere further to walk.
        if (nextPath === POST_SWITCH_FALLBACK_PATH) {
          teardownListener();
        }
        router.replace(nextPath);
      };

      try {
        trackActivity("app.action", {
          route: pathnameRef.current,
          action: "organization.switch",
          targetOrganizationId: organizationId,
        }, "Switched organization");

        const finishAuthTransition = beginAuthSessionTransition();
        try {
          // Stop in-flight queries before invalidating the current access
          // token on the server. Any request that still returns 401 during
          // the switch waits for the new session instead of refreshing the
          // old org-scoped token.
          await queryClient.cancelQueries();
          const authData = await switchActiveOrganization(organizationId);
          if (authData?.token) {
            setAccessToken(authData.token);
            if (typeof BroadcastChannel !== "undefined") {
              const channel = new BroadcastChannel("survey-loop-browser-session");
              channel.postMessage({ type: "session-changed" });
              window.setTimeout(() => channel.close(), 0);
            }
          }
        } finally {
          finishAuthTransition();
        }

        // Drop every cached query so org-scoped data (organizations,
        // surveys, responses, etc.) cannot leak across orgs. Query keys do
        // not include the active org id today, so this is the safest reset.
        queryClient.removeQueries();

        // Unstick OrganizationContext's locally pinned organization so the
        // header switcher reflects the new active org.
        clearSelectedOrganization();

        // Arm the 404 listener BEFORE re-running the route's data fetches
        // so we can react to "resource doesn't exist in new org" errors.
        const queryCache = queryClient.getQueryCache();
        unsubscribe = queryCache.subscribe((event) => {
          if (event.type !== "updated") return;
          const error = event.query.state.error;
          if (isNotFoundError(error)) {
            handle404();
          }
        });
        // Safety net: tear down the listener if no 404 fires within a few
        // seconds. Long enough for slow networks, short enough to avoid
        // surprising redirects from unrelated 404s later in the session.
        teardownTimer = setTimeout(teardownListener, 8000);

        // Refetch the session so user.activeOrganization reflects the new
        // org; OrganizationContext's currentOrganizationQuery will then
        // refetch under the new key.
        await checkAuth();

        // Re-run server components / route loaders for the current path.
        // Client-side React Query hooks will refetch on next render thanks
        // to the cache removal above.
        router.refresh();
      } catch (error) {
        teardownListener();
        console.error("Failed to switch organization", error);
      } finally {
        setIsSwitchingOrganization(false);
      }
    },
    [
      checkAuth,
      clearSelectedOrganization,
      isSwitchingOrganization,
      queryClient,
      router,
    ],
  );

  return (
    <div className="authenticated-layout">
      {isImpersonating && (
        <div className="impersonation-bar">
          <span className="impersonation-bar__text">
            Impersonating {user?.name} ({user?.email})
          </span>
          <Button
            variant="primary"
            size="sm"
            className="impersonation-bar__stop"
            onClick={stopImpersonating}
          >
            Stop impersonating
          </Button>
        </div>
      )}

      <Navigation
        variant="light"
        showSuperAdminTag={user?.isSuperAdmin}
        secondaryItems={
          <>
            {user?.isSuperAdmin && (
              <Link href="/admin">
                <NavigationItem
                  isActive={pathname.startsWith("/admin")}
                  leadingVisual="Settings"
                >
                  Admin
                </NavigationItem>
              </Link>
            )}

            <Link href="/organizations">
              <NavigationItem
                isActive={pathname === "/organizations"}
                leadingVisual="Building"
              >
                Organization
              </NavigationItem>
            </Link>

            {userOrganizations.length > 0 && (
              <NavigationItemDropdown
                variant="transparent"
                dropdownContent={
                  <>
                    {isOrgSearchEnabled && (
                      <div className="org-switcher__search-wrapper">
                        <InputText
                          ref={orgSearchRef}
                          value={orgSearchQuery}
                          size="sm"
                          disableLayoutAnimation
                          onChange={(e) => setOrgSearchQuery(e.target.value)}
                          placeholder="Search organizations..."
                          leadingVisual="Search"
                        />
                      </div>
                    )}
                    {filteredUserOrganizations.length === 0 ? (
                      <div className="org-switcher__no-results" aria-live="polite">
                        No organizations match &ldquo;{orgSearchQuery}&rdquo;
                      </div>
                    ) : (
                      filteredUserOrganizations.map((organization) => (
                        <DropdownItem
                          key={organization._id}
                          leadingVisual="Building"
                          selected={organization.isActiveOrganization}
                          onClick={() => handleSwitchOrganization(organization._id)}
                        >
                          {getOrganizationLabel(organization)}
                          {organization.isHomeOrganization ? " • Home" : ""}
                          {organization.assignmentType === "INHERITED" ? " • Inherited" : ""}
                        </DropdownItem>
                      ))
                    )}
                  </>
                }
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <Icon name="Building" size="1" />
                  <span>{currentOrganization?.name || "Organization"}</span>
                  {isSwitchingOrganization ? "…" : ""}
                </span>
              </NavigationItemDropdown>
            )}

            {inAppNotificationsEnabled ? <NotificationCenter /> : null}

            <IconButton
              variant="transparent"
              className="navigation__icon-btn"
              aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
              onClick={toggleColorMode}
              leadingVisual={isDarkMode ? "Sun" : "Moon"}
            />

            <NavigationItemDropdown
              variant="transparent"
              dropdownContent={
                <div className="user-menu">
                  {(user?.name || user?.email) && (
                    <div className="user-menu__identity" role="presentation">
                      {user?.name && (
                        <div className="user-menu__identity-name" title={user.name}>
                          {user.name}
                        </div>
                      )}
                      {user?.email && (
                        <div className="user-menu__identity-email" title={user.email}>
                          {user.email}
                        </div>
                      )}
                    </div>
                  )}
                  <DropdownItem leadingVisual="User" onClick={() => router.push("/settings/account")}>
                    Account Settings
                  </DropdownItem>
                  {inAppNotificationsEnabled ? (
                    <DropdownItem leadingVisual="Bell" onClick={() => router.push("/settings/notifications")}>
                      Notification Settings
                    </DropdownItem>
                  ) : null}
                  <DropdownItem leadingVisual="Globe" onClick={() => router.push("/settings/domain-email")}>
                    Domain & Email
                  </DropdownItem>
                  <DropdownItem leadingVisual="File" onClick={() => router.push("/settings/files")}>
                    Files
                  </DropdownItem>
                  <DropdownItem leadingVisual="Key" onClick={() => router.push("/settings/api")}>
                    API Tokens
                  </DropdownItem>
                  <DropdownItem leadingVisual="BookOpen" onClick={() => router.push("/settings/api-docs")}>
                    API Docs
                  </DropdownItem>
                  {canManageOrgResources ? (
                    <DropdownItem leadingVisual="Plug" onClick={() => router.push("/settings/integrations")}>
                      Integrations
                    </DropdownItem>
                  ) : null}
                  {canManageOrgResources ? (
                    <DropdownItem
                      leadingVisual="QrCode"
                      onClick={() => router.push("/qr-code-templates")}
                    >
                      QR Code Templates
                    </DropdownItem>
                  ) : null}
                  <DropdownItem leadingVisual="LogOut" onClick={handleLogout}>
                    Log Out
                  </DropdownItem>
                </div>
              }
            >
              <Avatar
                name={user?.name || "User"}
                size="xs"
                variant="neutral"
              />
            </NavigationItemDropdown>

            <IconButton
              variant="transparent"
              className="navigation__hamburger"
              aria-label="Open menu"
              onClick={() => setMobileNavOpen(true)}
              leadingVisual="Menu"
            />
          </>
        }
      >
        <Navigation.Logo>
          {isWhiteLabelEnabled && currentOrganization?.orgLogoUrl ? (
            <Image
              src={currentOrganization.orgLogoUrl}
              alt={
                currentOrganization?.name
                  ? `${currentOrganization.name} logo`
                  : "Logo"
              }
              width={110}
              height={28}
            />
          ) : (
            <SurveyLoopLogo />
          )}
        </Navigation.Logo>
        <Link href="/dashboard">
          <NavigationItem
            isActive={pathname === "/dashboard"}
            leadingVisual="Home"
          >
            Dashboard
          </NavigationItem>
        </Link>
        <Link href="/surveys">
          <NavigationItem
            isActive={pathname === "/surveys" || pathname.startsWith("/surveys/")}
            leadingVisual="FileText"
          >
            Surveys
          </NavigationItem>
        </Link>
        <Link href="/responses">
          <NavigationItem
            isActive={pathname.startsWith("/responses")}
            leadingVisual="BarChart"
          >
            Responses
          </NavigationItem>
        </Link>
        {canManageOrgResources && googleReviewsListingsEnabled ? (
          <>
            <Link href="/reviews">
              <NavigationItem
                isActive={pathname.startsWith("/reviews")}
                leadingVisual="Star"
              >
                Reviews
              </NavigationItem>
            </Link>
            <Link href="/listings">
              <NavigationItem
                isActive={pathname.startsWith("/listings")}
                leadingVisual="MapPinned"
              >
                Listings
              </NavigationItem>
            </Link>
          </>
        ) : null}
        <Link href="/analytics">
          <NavigationItem
            isActive={pathname.startsWith("/analytics")}
            leadingVisual="BarChart3"
          >
            Analytics
          </NavigationItem>
        </Link>
      </Navigation>

      <LoopyPageContextProvider
        activeOrganizationId={currentOrganizationId ? String(currentOrganizationId) : null}
        activeOrganizationName={currentOrganization?.name || null}
      >
        <main className="authenticated-layout__main">{children}</main>
        <AuthenticatedActivityTracker />
        <GuidedOnboardingPanel />
        <SupportChatWidget
          enabled={supportChatEnabled}
          agentEnabled={surveyLoopAgentEnabled}
        />
      </LoopyPageContextProvider>

      {isMobile && (
        <Drawer.Root
          direction="bottom"
          open={mobileNavOpen}
          onOpenChange={setMobileNavOpen}
        >
          <Drawer.Overlay />
          <Drawer.Content>
            <Drawer.Handle />
            <div className="mobile-nav-drawer">
              <nav className="mobile-nav-drawer__section">
                {navLinks.map((link) => (
                  <Button
                    key={link.href}
                    variant="transparent"
                    className={`mobile-nav-drawer__link${link.isActive ? " mobile-nav-drawer__link--active" : ""}`}
                    onClick={() => navigateAndClose(link.href)}
                    leadingVisual={link.icon}
                  >
                    {link.label}
                  </Button>
                ))}
              </nav>

              <div className="mobile-nav-drawer__divider" />

              <div className="mobile-nav-drawer__section">
                <Button
                  variant="transparent"
                  className={`mobile-nav-drawer__link${pathname === "/organizations" ? " mobile-nav-drawer__link--active" : ""}`}
                  onClick={() => navigateAndClose("/organizations")}
                  leadingVisual="Building"
                >
                  Organization
                </Button>
                {user?.isSuperAdmin && (
                  <Button
                    variant="transparent"
                    className={`mobile-nav-drawer__link${pathname.startsWith("/admin") ? " mobile-nav-drawer__link--active" : ""}`}
                    onClick={() => navigateAndClose("/admin")}
                    leadingVisual="Settings"
                  >
                    Admin
                    <span className="navigation__admin-tag" style={{ marginLeft: "auto" }}>
                      Super Admin
                    </span>
                  </Button>
                )}
                <Button
                  variant="transparent"
                  className={`mobile-nav-drawer__link${pathname === "/settings/account" ? " mobile-nav-drawer__link--active" : ""}`}
                  onClick={() => navigateAndClose("/settings/account")}
                  leadingVisual="User"
                >
                  Account Settings
                </Button>
                {inAppNotificationsEnabled ? (
                  <Button
                    variant="transparent"
                    className={`mobile-nav-drawer__link${pathname === "/settings/notifications" ? " mobile-nav-drawer__link--active" : ""}`}
                    onClick={() => navigateAndClose("/settings/notifications")}
                    leadingVisual="Bell"
                  >
                    Notification Settings
                  </Button>
                ) : null}
                <Button
                  variant="transparent"
                  className={`mobile-nav-drawer__link${pathname === "/settings/domain-email" ? " mobile-nav-drawer__link--active" : ""}`}
                  onClick={() => navigateAndClose("/settings/domain-email")}
                  leadingVisual="Globe"
                >
                  Domain & Email
                </Button>
                <Button
                  variant="transparent"
                  className={`mobile-nav-drawer__link${pathname === "/settings/files" ? " mobile-nav-drawer__link--active" : ""}`}
                  onClick={() => navigateAndClose("/settings/files")}
                  leadingVisual="File"
                >
                  Files
                </Button>
                <Button
                  variant="transparent"
                  className={`mobile-nav-drawer__link${pathname === "/settings/api" ? " mobile-nav-drawer__link--active" : ""}`}
                  onClick={() => navigateAndClose("/settings/api")}
                  leadingVisual="Key"
                >
                  API Tokens
                </Button>
                <Button
                  variant="transparent"
                  className={`mobile-nav-drawer__link${pathname === "/settings/api-docs" ? " mobile-nav-drawer__link--active" : ""}`}
                  onClick={() => navigateAndClose("/settings/api-docs")}
                  leadingVisual="BookOpen"
                >
                  API Docs
                </Button>
                {canManageOrgResources ? (
                  <Button
                    variant="transparent"
                    className={`mobile-nav-drawer__link${pathname === "/settings/integrations" ? " mobile-nav-drawer__link--active" : ""}`}
                    onClick={() => navigateAndClose("/settings/integrations")}
                    leadingVisual="Plug"
                  >
                    Integrations
                  </Button>
                ) : null}
                {canManageOrgResources ? (
                  <Button
                    variant="transparent"
                    className={`mobile-nav-drawer__link${pathname.startsWith("/qr-code-templates") ? " mobile-nav-drawer__link--active" : ""}`}
                    onClick={() => navigateAndClose("/qr-code-templates")}
                    leadingVisual="QrCode"
                  >
                    QR Code Templates
                  </Button>
                ) : null}
              </div>

              <div className="mobile-nav-drawer__divider" />

              <div className="mobile-nav-drawer__section">
                <div className="mobile-nav-drawer__toggle-row">
                  <div className="mobile-nav-drawer__toggle-label">
                    <Icon name={isDarkMode ? "Moon" : "Sun"} size={20} />
                    <span>Dark mode</span>
                  </div>
                  <Toggle
                    checked={isDarkMode}
                    onChange={toggleColorMode}
                    ariaLabel="Toggle dark mode"
                  />
                </div>
              </div>

              <div className="mobile-nav-drawer__divider" />

              <div className="mobile-nav-drawer__section">
                <Button
                  variant="transparent"
                  className="mobile-nav-drawer__link mobile-nav-drawer__link--danger"
                  onClick={() => {
                    setMobileNavOpen(false);
                    handleLogout();
                  }}
                  leadingVisual="LogOut"
                >
                  Log Out
                </Button>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Root>
      )}
    </div>
  );
};
